import type {
  TeamChatJoinParams,
  TeamChatJoinResponse,
} from "@bezoned/shared/types/api/team/team-chat-join.types.js";
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";

import { useAuth } from "../auth/use-auth.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { useOffice } from "../office/use-office.js";
import { resolveCurrentTable } from "./resolve-current-table.js";
import { useChatChannel } from "./use-chat-channel.js";
import { useCurrentTableId } from "./use-current-table-id.js";

type RefreshTokenFn = () => Promise<{ token: string; expiresOn: Date }>;

interface ChatState {
  officeThreadId: string | null;
  officeCredentials: TeamChatJoinResponse | null;
  officeTopic: string | null;
  isOfficeLoading: boolean;
  isOfficeInitError: boolean;
  retryOfficeChat: () => void;
  officeRefreshToken: RefreshTokenFn | null;
  tableChatThreadId: string | null;
  tableCredentials: TeamChatJoinResponse | null;
  tableTopic: string;
  isLoadingTableChat: boolean;
  isTableInitError: boolean;
  retryTableChat: () => void;
  tableRefreshToken: RefreshTokenFn | null;
}

const ChatStateContext = createContext<ChatState | null>(null);

export const ChatStateProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { sharedTree } = useFluid();
  const { team, tables } = useOffice();

  const currentTableId = useCurrentTableId(user.id, sharedTree);
  const currentTable = useMemo(
    () => resolveCurrentTable(currentTableId, tables),
    [currentTableId, tables]
  );

  const officeChatParams = useMemo<TeamChatJoinParams | null>(
    () =>
      team?.acsThreadId && team?.msTeamId ? { msTeamId: team.msTeamId } : null,
    [team?.acsThreadId, team?.msTeamId]
  );

  const tableChatParams = useMemo<TeamChatJoinParams | null>(
    () =>
      currentTable?.id && team?.msTeamId
        ? { msTeamId: team.msTeamId, tableId: currentTable.id }
        : null,
    [currentTable?.id, team?.msTeamId]
  );

  const officeChat = useChatChannel({ params: officeChatParams });
  const tableChat = useChatChannel({ params: tableChatParams });

  const officeTopic = "Office Chat";
  const tableTopic = currentTable?.name?.trim() || "Table Chat";

  const value = useMemo<ChatState>(
    () => ({
      isLoadingTableChat: tableChat.isLoading,
      isOfficeInitError: officeChat.isError,
      isOfficeLoading: officeChat.isLoading,
      isTableInitError: tableChat.isError,
      officeCredentials: officeChat.credentials,
      officeRefreshToken: officeChat.refreshToken,
      officeThreadId: officeChat.threadId,
      officeTopic,
      retryOfficeChat: officeChat.retry,
      retryTableChat: tableChat.retry,
      tableChatThreadId: tableChat.threadId,
      tableCredentials: tableChat.credentials,
      tableRefreshToken: currentTable?.acsThreadId
        ? tableChat.refreshToken
        : null,
      tableTopic,
    }),
    [
      officeChat.threadId,
      officeChat.credentials,
      officeChat.isLoading,
      officeChat.isError,
      officeChat.retry,
      officeChat.refreshToken,
      tableChat.threadId,
      tableChat.credentials,
      tableChat.isLoading,
      tableChat.isError,
      tableChat.retry,
      tableChat.refreshToken,
      tableTopic,
      currentTable?.acsThreadId,
    ]
  );

  return (
    <ChatStateContext.Provider value={value}>
      {children}
    </ChatStateContext.Provider>
  );
};

export const useChatState = () => {
  const context = useContext(ChatStateContext);
  if (!context) {
    throw new Error("useChatState must be used within a ChatStateProvider");
  }
  return context;
};
